import { getFontFamilyWithFallbacks, parseFontStr } from '@wix/thunderbolt-becky-root/src/functionLibraryExtensions'
import { Component, Style } from '@wix/thunderbolt-becky-types'
import { createCompNode, themeRefs } from '@wix/thunderbolt-catharsis'
import { textPresetKeys } from '@wix/thunderbolt-commons'
import { EMPTY_ARRAY } from '../../../utils/constants'
import { isTpaComponent } from '../../../utils/is'
import { FeatureRefs } from '../../cssFeatures.types'

type FontJSONFromStyleString = {
	family: string
	style: {
		bold: boolean
		italic: boolean
		underline: boolean
	}
	size: number
	preset: string
	editorKey: string
	fontStyleParam: boolean
	fontParam: boolean
	cssFontFamily: string
	value: string
	theme: string
}

type FontJSON = {
	family: string
	style: {
		bold: boolean
		italic: boolean
		underline: boolean
	}
	size: string | number
	preset: string
	editorKey: string
	fontStyleParam: boolean
	fontParam: boolean
	cssFontFamily: string
	value: string
	theme: string
}

type FontObject = {
	editorKey: string
	lineHeight: string
	style: string
	weight: string
	size: string
	fontFamily: string
	value: string
}

const parseJSON = (val: string) => {
	if (!val) {
		return val
	}

	try {
		return JSON.parse(val)
	} catch (e) {
		if (process.env.NODE_ENV === 'development') {
			console.warn('parseJSON failed parsing value:', val)
		}
		return null
	}
}

const doubleQuotesEscapeForIE = (str: string): string => str.replace(/''/g, "'")

const stylePropertiesRegex = /^param_([^_]+)_(.*)/

const updateCustomSavedFontValue = (fontJson: FontJSONFromStyleString): FontJSON => {
	const family = fontJson.family
	const fontFamilyWithFallbacks = getFontFamilyWithFallbacks(family)
	const fontSize = fontJson.size
	const size = `${fontSize}px`
	const lineHeight = `${Math.floor(fontSize * 1.25)}px`
	const style = fontJson.style
	const italic = style?.italic ? 'italic' : 'normal'
	const weight = style?.bold ? 'bold' : 'normal'
	const variant = 'normal'
	const underline = style?.underline ? 'text-decoration:underline;' : ''
	const value = createFontValue(italic, variant, weight, size, lineHeight, family, underline)
	return { ...fontJson, value, family: fontFamilyWithFallbacks }
}

const updatePresetSelectedFromNewUILib = (fontJson: FontJSONFromStyleString): FontJSON => {
	const themeFont = parseFontStr(fontJson.preset)
	const size = `${themeFont.size}px`
	const variant = themeFont.variant
	const family = themeFont.family[0].toLowerCase()
	const lineHeight = themeFont.lineHeight
	const bold = themeFont.weight
	const italic = themeFont.style

	return {
		...fontJson,
		size,
		style: { bold: bold === 'bold', italic: italic === 'italic', underline: false },
		value: createFontValue(italic, variant, bold, size, lineHeight, family, ''),
	}
}

const updateSavedFontValueFromNewUILib = (fontJson: FontJSONFromStyleString): FontJSON => {
	const fontWithFallbacks = getFontFamilyWithFallbacks(fontJson.family)
	const italic = fontJson.style?.italic ? 'italic' : 'normal'
	const weight = fontJson.style?.bold ? 'bold' : 'normal'
	const underline = fontJson.style?.underline ? ';text-decoration:underline;' : ';'
	const value = `font-family:${fontWithFallbacks};font-styles:${italic};font-weight:${weight}${underline}`
	return { ...fontJson, value }
}

const createFontValue = (
	italic: string,
	variant: string,
	weight: string,
	size: string,
	lineHeight: string,
	family: string,
	underline: string
): string => {
	const fontFamilyWithFallbacks = getFontFamilyWithFallbacks(family)
	return `font:${italic} ${variant} ${weight} ${size}/${lineHeight} ${fontFamilyWithFallbacks};${underline}`
}

const updateSavedFontValue = (fontJson: FontJSONFromStyleString): FontJSON => {
	const fontFamily = fontJson.cssFontFamily || fontJson.value
	return {
		...fontJson,
		size: 0,
		style: { bold: false, italic: false, underline: false },
		family: fontJson.value,
		value: doubleQuotesEscapeForIE(`font-family:${fontFamily}`),
	}
}

const createFontObject = (font: string, editorKey: string): FontObject => {
	const fontObj = parseFontStr(font)

	const sizePx = `${fontObj.size}px`
	return {
		editorKey,
		lineHeight: fontObj.lineHeight,
		style: fontObj.style,
		weight: fontObj.weight,
		size: sizePx,
		fontFamily: fontObj.family.join(',').toLowerCase(),
		value: createFontValue(
			fontObj.style,
			fontObj.variant,
			fontObj.weight,
			sizePx,
			fontObj.lineHeight,
			fontObj.family.join(','),
			''
		),
	}
}

export const getTextPresets = (themeFonts: Array<string>) =>
	textPresetKeys
		.filter((_val, index) => themeFonts[index])
		.map((val, index) => ({
			name: val,
			editorKey: `font_${index}`,
			font: themeFonts[index],
		}))
		.reduce<Record<string, FontObject>>((acc, val) => {
			acc[val.name] = createFontObject(val.font, val.editorKey)
			return acc
		}, {})

const updatePresetSavedFontValue = (fontJson: FontJSONFromStyleString, themeFonts: Array<string>): FontJSON => {
	const themeFont = getTextPresets(themeFonts)[fontJson.preset]
	const size = parseInt(themeFont.size, 10)
	const family = themeFont.fontFamily
	const bold = themeFont.weight
	const italic = themeFont.style
	const value = themeFont.value

	return {
		...fontJson,
		size,
		family,
		style: { bold: bold === 'bold', italic: italic === 'italic', underline: false },
		value,
	}
}

const getTpaSinglePropertyFontFamilies = (
	stylePropertyKey: string,
	stylePropertyValue: string,
	themeFonts: Array<string>
): FontJSON | null => {
	const match = stylePropertyKey.match(stylePropertiesRegex)

	const isFontStyleParam = match && match[1] === 'font' && stylePropertyValue

	if (!isFontStyleParam) {
		return null
	}

	const parsedJson = parseJSON(stylePropertyValue)

	if (!parsedJson) {
		return null
	}

	if (parsedJson?.fontStyleParam) {
		if (parsedJson?.preset === 'Custom') {
			return updateCustomSavedFontValue(parsedJson)
		}

		if (parsedJson?.theme) {
			return updatePresetSelectedFromNewUILib(parsedJson)
		}

		return updatePresetSavedFontValue(parsedJson, themeFonts)
	}

	if (parsedJson?.fontParam) {
		if (parsedJson?.preset === 'None') {
			return updateSavedFontValueFromNewUILib(parsedJson)
		}

		return updateSavedFontValue(parsedJson)
	}

	return parsedJson
}

export const getTpaFontFamilies = (style: Style, themeFonts: Array<string>): Array<string> => {
	const styleProperties = style.style?.properties
	if (!styleProperties) {
		return EMPTY_ARRAY
	}

	const fontFamilies: Array<string> = []
	for (const stylePropertyKey in styleProperties) {
		const value = getTpaSinglePropertyFontFamilies(stylePropertyKey, styleProperties[stylePropertyKey], themeFonts)

		if (value?.family) {
			fontFamilies.push(...value.family.split(','))
		}
	}
	return fontFamilies
}

export const tpaFontFamilies = {
	predicate: (component: Component) => isTpaComponent(component),
	compNode: createCompNode({
		getDependencies: (component: Component, refs: FeatureRefs<'fontFaces'>) => ({
			compStyles: refs.compStyles(component),
			siteFonts: themeRefs.siteFonts,
		}),
		toViewItem: (__, { compStyles, siteFonts }) => {
			if (!compStyles) {
				return EMPTY_ARRAY
			}
			const getFonts = (style: Style) => getTpaFontFamilies(style, siteFonts)
			if (compStyles.type === 'classic') {
				return Object.values(compStyles.css).flatMap(getFonts)
			}
			return Object.values(compStyles.css).flatMap((variants) => Object.values(variants).flatMap(getFonts))
		},
	}),
}
